import { Component, OnInit, ViewChild, inject, ElementRef, Input, DestroyRef, HostListener } from '@angular/core';
import { MatError, MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PaymentMethodModel } from '../../../customer/customer.model';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { TransactionsService } from 'app/features/transactions/services/transactions.service';
import { SecureClientService } from 'app/secure-client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthStore } from 'app/features/user/store/auth.store';
import { UserStore } from 'app/features/user/store/user.store';
import { DEBIT_NOT_ACCEPTED_MESSAGE, isDebitNotAccepted } from 'app/shared/utils/debit-card.util';
import {
  expirationDateMaxValidator,
  expirationDateValidator,
  expirationFormatValidator,
  normalizeExpirationToMMYY,
} from 'app/shared/utils/card-validators';

type CardDetailsFormType = FormGroup<CardDetailsFormControlsType>;

type CardDetailsFormControlsType = {
  account: FormControl<string | null>;
  cardHolderName: FormControl<string | null>;
  expirationDate: FormControl<string | null>;
  cvv: FormControl<string | null>;
  streetAddress: FormControl<string | null>;
  zipCode: FormControl<string | null>;
  isDefault: FormControl<boolean | null>;
  ignoreCVV: FormControl<boolean | null>;
  ignoreZip: FormControl<boolean | null>;
  ignoreStreet: FormControl<boolean | null>;
  trackData: FormControl<string | null>;
};

/**
 * Shown when the processor declines the card itself (`validCard: false`), e.g. HOLD-CALL.
 * The operator is usually on the phone with the customer, so say what to do next.
 */
export const CARD_DECLINED_MESSAGE =
  'There is a problem with the credit card as entered. Please ask the customer for an alternate payment card, or ask the customer to contact their bank.';

/** The declined-card message, with the processor's own code (e.g. HOLD-CALL) when it gave one. */
export function cardDeclinedMessage(processorMessage?: string | null): string {
  const code = (processorMessage ?? '').trim();
  return code ? `${CARD_DECLINED_MESSAGE} (${code})` : CARD_DECLINED_MESSAGE;
}

@Component({
  selector: 'app-card-details-form',
  standalone: true,
  imports: [
    CommonModule,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MatPrefix,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatSlideToggle,
    NgxMaskDirective,
  ],
  templateUrl: './card-details-form.component.html',
  styles: [
    `
      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
  host: {
    class: 'app-card-details-form',
  },
  providers: [provideNgxMask()],
})
export class CardDetailsFormComponent implements OnInit {
  // @HostListener('document:keydown', ['$event']) onKeydownHandler(event: KeyboardEvent) {
  //   if (event.key === 'Enter' && this.cardSwipeInProgress) {
  //     // Process the card swipe data
  //     const cardData = this.cardSwipeData.trim();

  //     // Assuming card data is in Track 1 or Track 2 format
  //     const track1Pattern = /^%B(\d{13,19})\^([^^]+)\^(\d{4})/;
  //     const track2Pattern = /^;(\d{13,19})=(\d{4})/;

  //     let match = cardData.match(track1Pattern) || cardData.match(track2Pattern);
  //     debugger;
  //     if (match) {
  //     const account = match[1];
  //     const expirationDate = match[3];
  //     const cardHolderName = match[2]?.split('/').reverse().join(' ') || '';

  //     this.form.patchValue({
  //       account,
  //       expirationDate,
  //       cardHolderName
  //     });

  //     this.onAccountFilled();
  //     }

  //     // Reset swipe mode and data
  //     this.cardSwipeInProgress = false;
  //     this.cardSwipeData = '';
  //   } else if (this.cardSwipeInProgress) {
  //     event.preventDefault();
  //     if(event.key != 'Shift') {
  //     // Append the key to the swipe data
  //       this.cardSwipeData += event.key;
  //     }
  //   } else if (event.key === '%') {
  //     // Start of card swipe
  //     this.cardSwipeInProgress = true;
  //     this.cardSwipeData = event.key;
  //   }

  //   // debugger;
  //   // event.preventDefault();
  // }
  @ViewChild('expirationDateInput') expirationDateInput!: ElementRef;
  @ViewChild('cvvInput') cvvInput!: ElementRef;
  @ViewChild('accountInput') accountInput!: ElementRef;
  @ViewChild('cardHolderNameInput') cardHolderNameInput!: ElementRef;
  @ViewChild('addressInput') addressInput!: ElementRef;
  @ViewChild('zipInput') zipInput!: ElementRef;

  private destroyRef = inject(DestroyRef);

  cardSwipeInProgress = false;
  cardSwipeData = '';

  private userStore: any;
  private authStore: any;

  data: any;
  accPrefix = '';
  cardMask = '9';
  cvvMask = '000';
  isViewMode = false;
  isEditMode = false;
  cardType = '';
  cardFunding = '';
  cardBank = '';
  cardBin = '';
  isCardValidated = false;

  isCardInvalid = false;
  isCvvInvalid = false;
  hasCvvBeenChecked = false;
  showIgnoreCVV = false;
  isAvsInvalid = false;
  isZipInvalid = false;
  hasZipBeenChecked = false;
  showIgnoreZip = false;
  isStreetInvalid = false;
  hasStreetBeenChecked = false;
  showIgnoreStreet = false;
  invalidMessage = '';
  hideCardNumber = true;
  private verifyDebounceTimer: any = null;
  private processingResponse = false;
  private isVerifyingCard = false;

  /**
   * The card auto-verifies exactly once per card. This gate is set the first time a
   * verification actually fires and is reset when the card number (PAN) changes so a
   * brand-new card can auto-verify once again. All other re-verifications are manual
   * (the "Verify Card" button).
   */
  private hasAutoVerified = false;
  private lastVerifiedPan = '';

  /** True when card verification is in progress (debounce pending or API call in flight) */
  get isVerificationPending(): boolean {
    return this.isVerifyingCard || !!this.verifyDebounceTimer;
  }

  /**
   * Shows the manual "Verify Card" button. It appears only after the single auto-verify
   * has already happened, whenever the current locally-valid data still needs verifying
   * (last attempt failed, or a field changed since the last check). Hidden while a
   * verification is pending/in-flight.
   */
  get showVerifyCardButton(): boolean {
    return (
      this.hasAutoVerified &&
      !this.isVerifyingCard &&
      !this.verifyDebounceTimer &&
      this.isLocallyValid() &&
      (!this.isCardValidated || this.dataChangedSinceLastCheck())
    );
  }

  enforceAVSMatch = false;
  requireCVV = true;
  /** Profile `blockDebitCards`; FTE also flags a refused card with `debitNotAccepted`. */
  blockDebitCards = false;
  isDebitRefused = false;

  lastChecked: {
    account: string;
    cardHolderName: string;
    expirationDate: string;
    streetAddress: string;
    zipCode: string;
    cvv: string;
  } = {
    account: '',
    cardHolderName: '',
    expirationDate: '',
    streetAddress: '',
    zipCode: '',
    cvv: '',
  };

  @Input() hideDefaultOption = false;

  form: CardDetailsFormType = new FormGroup<CardDetailsFormControlsType>({
    account: new FormControl(null, [
      Validators.required,
      Validators.pattern(
        /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|6(?:011|5[0-9]{2})[0-9]{12}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
      ),
      this.luhnValidator,
      this.cardInvalidValidator.bind(this),
    ]),
    cardHolderName: new FormControl(null, [Validators.required]),
    expirationDate: new FormControl(null, [
      Validators.required,
      expirationFormatValidator,
      expirationDateValidator,
      expirationDateMaxValidator,
    ]),
    // cvv: new FormControl(null, [Validators.required]),
    cvv: new FormControl(null, [Validators.required, this.cvvInvalidValidator.bind(this)]),
    streetAddress: new FormControl(null, {
      validators: [Validators.required, this.streetInvalidValidator.bind(this) as any, Validators.maxLength(50)],
    } as any),
    zipCode: new FormControl(null, [
      Validators.required,
      Validators.pattern(/^\d{5}$/),
      Validators.minLength(5),
      Validators.maxLength(5),
      this.zipInvalidValidator.bind(this),
    ]),
    isDefault: new FormControl(true),
    ignoreCVV: new FormControl(false),
    ignoreZip: new FormControl(false),
    ignoreStreet: new FormControl(false),
    trackData: new FormControl(null),
  });

  luhnValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    const digits = control.value.replace(/\D/g, '');
    if (digits.length === 0) {
      return null;
    }
    let sum = 0;
    let alternate = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = parseInt(digits[i], 10);
      if (alternate) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alternate = !alternate;
    }
    return sum % 10 === 0 ? null : { luhnInvalid: true };
  }

  /**
   *
   * @param control
   * @returns
   * @remarks This validator is for marking the card number field as invalid based on backend validation results, without blocking the validation request itself (which would create a catch-22). The actual invalid state is controlled by the `isCardInvalid` flag which is set from the backend response. This allows us to show backend validation errors on the form without interfering with the user's ability to correct them and resubmit.
   */
  cardInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isCardInvalid) {
      return { cardInvalid: true };
    }
    return null;
  }

  zipInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.form?.get('ignoreZip')?.value == true) {
      return null;
    }
    if (this.isZipInvalid && this.hasZipBeenChecked) {
      return { zipInvalid: true };
    }
    return null;
  }

  streetInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.form?.get('ignoreStreet')?.value == true) {
      return null;
    }
    if (this.isStreetInvalid && this.hasStreetBeenChecked) {
      return { streetInvalid: true };
    }
    return null;
  }

  cvvInvalidValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.form?.get('ignoreCVV')?.value == true) {
      return null;
    }
    if (this.isCvvInvalid && this.hasCvvBeenChecked) {
      return { cvvInvalid: true };
    }
    return null;
  }

  getErrors() {
    return JSON.stringify(this.form.get('streetAddress')?.errors);
  }

  ignoreCVVValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isCvvInvalid && this.form.get('ignoreCVV')?.value == false) {
      return { ignoreCVVInvalid: true };
    }
    return null;
  }

  ignoreZipValidator(control: FormControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null;
    }
    if (this.isAvsInvalid && this.form.get('ignoreZip')?.value == false) {
      return { ignoreZipInvalid: true };
    }
    return null;
  }

  private secureClient = inject(SecureClientService);
  constructor(protected transactionsService: TransactionsService) {
    this.authStore = inject(AuthStore);
    this.userStore = inject(UserStore);
  }

  async onAccountFilled() {
    if (this.isEditMode) return;
    if (!this.isCardInvalid) {
      this.expirationDateInput.nativeElement.focus();
    }
  }

  onAccountPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const raw = event.clipboardData?.getData('text') ?? '';
    const digits = raw.replace(/\D/g, '').slice(0, 19);
    if (!digits) return;

    switch (digits.charAt(0)) {
      case '3':
        this.cardMask = '9000 00XXXX XX000';
        this.cvvMask = '0000';
        break;
      case '4':
      case '5':
      case '6':
        this.cardMask = '9000 00XX XXXX 0000';
        this.cvvMask = '000';
        break;
      default:
        this.cardMask = '99999999999999999';
        this.cvvMask = '000';
    }

    setTimeout(() => this.form.get('account')?.setValue(digits), 0);
  }

  ngOnInit(): void {
    this.enforceAVSMatch = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.forceAVSMatch || false;
    this.requireCVV = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.requireCVV ?? true;
    this.blockDebitCards = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.blockDebitCards === true;

    if (!this.requireCVV && !this.isViewMode && !this.isEditMode) {
      this.form.controls.cvv.setValidators([this.cvvInvalidValidator.bind(this) as any]);
      this.form.controls.cvv.updateValueAndValidity({ emitEvent: false });
      this.form.get('ignoreCVV')?.setValue(true, { emitEvent: false });
    }

    // Modal framework nests config.data under this.data.data
    const modalData = this.data?.data || this.data;
    const payment = modalData?.payment;

    if (payment) {
      if (modalData.editMode) {
        this.isEditMode = true;
        // Disable account field and clear its validators before patching —
        // the masked card number (e.g. XXXX1234) will never pass Luhn/pattern.
        const accountCtrl = this.form.get('account')!;
        accountCtrl.clearValidators();
        accountCtrl.disable();
        accountCtrl.updateValueAndValidity();

        this.form.patchValue({
          account: payment.account,
          cardHolderName: payment.cardHolderName,
          expirationDate: normalizeExpirationToMMYY(payment.expirationDate),
          cvv: '',
          streetAddress: payment.streetAddress,
          zipCode: payment.zipCode,
          isDefault: payment.isDefault,
        });

        const brand = (payment.cardType || '').toString();
        this.cardType = brand;
        const isAmex = /amex|american/i.test(brand);
        this.cvvMask = isAmex ? '0000' : '000';
      } else {
        this.form.patchValue({
          ...{ cvv: '-' },
          ...payment,
        });
        this.isViewMode = true;
      }
    }

    this.form
      .get('ignoreStreet')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ignored) => {
        this.processingResponse = true;
        const streetCtrl = this.form.get('streetAddress')!;
        const streetValidator = this.streetInvalidValidator.bind(this) as any;
        if (ignored) {
          streetCtrl.clearValidators();
          streetCtrl.setValidators([streetValidator, Validators.maxLength(50)]);
          this.isStreetInvalid = false;
          this.hasStreetBeenChecked = true;
        } else {
          this.hasStreetBeenChecked = false;
          streetCtrl.setValidators([Validators.required, streetValidator, Validators.maxLength(50)]);
        }
        streetCtrl.updateValueAndValidity();
        this.processingResponse = false;
        this.updateCardValidated();
        this.recheckIfLocallyValid();
      });

    this.form
      .get('ignoreCVV')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.processingResponse = true;
        this.form.controls.cvv.updateValueAndValidity();
        this.processingResponse = false;
        this.updateCardValidated();
        this.recheckIfLocallyValid();
      });

    this.form
      .get('ignoreZip')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.processingResponse = true;
        this.form.controls.zipCode.updateValueAndValidity();
        this.form.controls.streetAddress.updateValueAndValidity();
        this.processingResponse = false;
        this.updateCardValidated();
        this.recheckIfLocallyValid();
      });

    // Reset validated flags when user changes the field value (skip during response processing)
    this.form
      .get('zipCode')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.processingResponse) return;
        if (this.hasZipBeenChecked) {
          this.hasZipBeenChecked = false;
          this.isZipInvalid = false;
          this.showIgnoreZip = false;
          this.form.get('ignoreZip')?.setValue(false, { emitEvent: false });
          this.lastChecked.zipCode = '';
          this.form.controls.zipCode.updateValueAndValidity();
          this.updateCardValidated();
        }
      });

    this.form
      .get('cvv')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.processingResponse) return;
        if (this.hasCvvBeenChecked) {
          this.hasCvvBeenChecked = false;
          this.isCvvInvalid = false;
          this.showIgnoreCVV = false;
          this.form.get('ignoreCVV')?.setValue(false, { emitEvent: false });
          this.lastChecked.cvv = '';
          this.form.controls.cvv.updateValueAndValidity();
          this.updateCardValidated();
        }
      });

    this.form
      .get('streetAddress')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.processingResponse) return;
        if (this.hasStreetBeenChecked) {
          this.hasStreetBeenChecked = false;
          this.isStreetInvalid = false;
          this.showIgnoreStreet = false;
          this.form.get('ignoreStreet')?.setValue(false, { emitEvent: false });
          this.lastChecked.streetAddress = '';
          this.form.controls.streetAddress.updateValueAndValidity();
          this.updateCardValidated();
        }
      });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.processingResponse) return;
      this.scheduleAutoVerify();
    });

    this.form.get('expirationDate')?.valueChanges.subscribe((value) => {
      // Only auto-advance when we have a complete, valid MMYY (digits only).
      // Avoid yanking focus on partial/spaced edits that look like length 4.
      const mmyy = normalizeExpirationToMMYY(value);
      if (mmyy.length === 4 && this.form.controls.expirationDate.valid && this.cvvInput?.nativeElement) {
        this.cvvInput.nativeElement.focus();
      }
    });
    this.form.get('account')?.valueChanges.subscribe((value) => {
      try {
        // When the card number changes, allow the new card to auto-verify once again.
        const normalizedPan = (value || '').toString().replace(/\D/g, '');
        if (this.hasAutoVerified && normalizedPan !== this.lastVerifiedPan) {
          this.hasAutoVerified = false;
        }
        // Clear card invalid state when user enters a NEW card that passes Luhn.
        // Only a changed number counts: updateValueAndValidity() re-emits the same value
        // right after a decline is recorded, and treating that as a new card wiped the
        // decline message (a HOLD-CALL 400 showed nothing at all).
        if (this.isCardInvalid && value && normalizedPan !== this.lastVerifiedPan) {
          const digits = value.replace(/\D/g, '');
          if (digits.length >= 13 && this.luhnValidator({ value: digits } as any) === null) {
            this.isCardInvalid = false;
            this.isCardValidated = false;
            this.invalidMessage = '';
            this.form.controls.account.updateValueAndValidity();
          }
        }

        if (value && value.length > 0) {
          switch (value.substring(0, 1)) {
            case '6':
            case '5':
            case '4':
              this.cardMask = '9000 00XX XXXX 0000';
              this.cvvMask = '000';
              if (value.length == 16) {
                this.onAccountFilled();
              }

              break;
            case '3':
              this.cardMask = '9000 00XXXX XX000';
              this.cvvMask = '0000';
              if (value.length == 15) {
                this.onAccountFilled();
              }

              break;
            default:
              this.cardMask = '99999999999999999';
              this.cvvMask = '000';
              break;
          }
        }
      } catch (e) {
        this.cardMask = '9000 00XX XXXX 0000';
        this.cvvMask = '000';
      }
    });
    this.form.get('cvv')?.valueChanges.subscribe((value) => {
      if (this.form.controls.cvv.valid) {
        this.cardHolderNameInput.nativeElement.focus();
      }
    });
    this.form.get('zipCode')?.valueChanges.subscribe((value) => {});
    this.form.get('isDefault')?.valueChanges.subscribe((value) => {
      //console.log('isDefault value changed:', value);
    });
  }

  private recheckIfLocallyValid(): void {
    if (this.isLocallyValid() && this.isFullyValidated) {
      this.checkCard('auto');
    }
  }

  updateCardValidated(): void {
    this.isCardValidated = this.isFullyValidated;
    if (this.isCardValidated) {
      this.invalidMessage = `${this.cardType} Card Verified`;
    } else if (!this.isCardInvalid && this.invalidMessage.includes('Card Verified')) {
      // Was verified but a field changed — clear the message
      this.invalidMessage = '';
    }
  }

  /**
   * Card is considered validated when:
   * - Card itself is valid
   * - CVV is (validated + passed) or bypassed
   * - Zip is (validated + passed) or bypassed
   * - Street is (validated + passed) or ignored or bypassed
   */
  get isFullyValidated(): boolean {
    if (this.isCardInvalid) return false;

    const cvvOk = this.form.get('ignoreCVV')?.value || (this.hasCvvBeenChecked && !this.isCvvInvalid);
    const zipOk = this.form.get('ignoreZip')?.value || (this.hasZipBeenChecked && !this.isZipInvalid);
    const streetOk = this.form.get('ignoreStreet')?.value || (this.hasStreetBeenChecked && !this.isStreetInvalid);

    return cvvOk && zipOk && streetOk;
  }

  toggleCardVisibility(): void {
    this.hideCardNumber = !this.hideCardNumber;
  }

  onStreetBlur(): void {
    // Street edits flow through the unified debounce like every other field —
    // no separate immediate/forced fire, so the auto-once rule is respected.
    this.scheduleAutoVerify();
  }

  onStreetInput(): void {
    // Handled by the unified debounce via form.valueChanges.
  }

  /**
   * Single unified debounce (1000ms) for the auto-verify path. Every field change
   * resets the timer; when it elapses, checkCard('auto') decides whether an
   * (at-most-once) verification actually fires.
   */
  private scheduleAutoVerify(): void {
    if (this.verifyDebounceTimer) {
      clearTimeout(this.verifyDebounceTimer);
      this.verifyDebounceTimer = null;
    }
    
    if (this.hasAutoVerified) return;
    if (!this.isLocallyValid()) return;
    this.verifyDebounceTimer = setTimeout(() => {
      this.verifyDebounceTimer = null;
      this.checkCard('auto');
    }, 1000);
  }

  /** Manual re-verification (Verify Card button) — bypasses the auto-once gate and the dedup guard. */
  onVerifyCardClick(): void {
    if (this.verifyDebounceTimer) {
      clearTimeout(this.verifyDebounceTimer);
      this.verifyDebounceTimer = null;
    }
    this.checkCard('manual');
  }

  /** True when the current locally-valid data differs from what was last sent for verification. */
  private dataChangedSinceLastCheck(): boolean {
    const ignoreStreet = this.form.get('ignoreStreet')?.value == true;
    const currentStreet = ignoreStreet ? '' : this.form.get('streetAddress')?.value || '';
    return (
      this.lastChecked.account != (this.form.get('account')?.value || '') ||
      this.lastChecked.cardHolderName != (this.form.get('cardHolderName')?.value || '') ||
      this.lastChecked.expirationDate != (this.form.get('expirationDate')?.value || '') ||
      this.lastChecked.zipCode != (this.form.get('zipCode')?.value || '') ||
      this.lastChecked.cvv != (this.form.get('cvv')?.value || '') ||
      this.lastChecked.streetAddress != currentStreet
    );
  }

  getEditFormData(): any {
    const modalData = this.data?.data || this.data;
    const payment = modalData?.payment;
    const formValue = this.form.getRawValue();
    const ignoreCVV = formValue.ignoreCVV;
    const ignoreZip = formValue.ignoreZip;
    const ignoreStreet = formValue.ignoreStreet;
    const expiration = normalizeExpirationToMMYY(formValue.expirationDate);
    return {
      tokenId: payment!.id,
      tokenType: 'CARD',
      expiration: expiration || undefined,
      cvv: ignoreCVV ? undefined : formValue.cvv || undefined,
      address: ignoreStreet ? undefined : formValue.streetAddress || undefined,
      zipCode: ignoreZip ? undefined : formValue.zipCode || undefined,
      cardHolderName: formValue.cardHolderName || undefined,
    };
  }

  getFormData(): PaymentMethodModel {
    const formValue = this.form.getRawValue();
    return {
      account: formValue.account!,
      cardHolderName: formValue.cardHolderName!,
      expirationDate: normalizeExpirationToMMYY(formValue.expirationDate) || formValue.expirationDate!,
      cvv: formValue.ignoreCVV ? undefined : formValue.cvv!,
      streetAddress: formValue.ignoreStreet ? undefined : formValue.streetAddress!,
      zipCode: formValue.ignoreZip ? undefined : formValue.zipCode!,
      isDefault: formValue.isDefault ?? false,
      ignoreCVV: formValue.ignoreCVV!,
      ignoreZip: formValue.ignoreZip!,
      ignoreStreet: formValue.ignoreStreet!,
    };
  }

  /**
   * Backend-driven error keys that should NOT block sending the validation request.
   * These are set by the backend response, not by local form validation.
   */
  private readonly BACKEND_ERROR_KEYS = ['cardInvalid', 'cvvInvalid', 'zipInvalid', 'streetInvalid'];

  /**
   * Returns true if a form control has only backend-driven errors (or no errors).
   * Returns false if it has any local validation error.
   */
  private hasOnlyBackendErrors(controlName: string): boolean {
    const errors = this.form.get(controlName)?.errors;
    if (!errors) return true;
    return Object.keys(errors).every((key) => this.BACKEND_ERROR_KEYS.includes(key));
  }

  /**
   * Returns true if all local (non-backend) validations pass.
   */
  private isLocallyValid(): boolean {
    const ignoreStreet = this.form.get('ignoreStreet')?.value == true;
    const ignoreCVV = this.form.get('ignoreCVV')?.value == true;
    const ignoreZip = this.form.get('ignoreZip')?.value == true;

    if (!this.hasOnlyBackendErrors('account')) return false;
    if (!this.hasOnlyBackendErrors('cardHolderName')) return false;
    if (!this.hasOnlyBackendErrors('expirationDate')) return false;
    if (!ignoreCVV && !this.hasOnlyBackendErrors('cvv')) return false;
    if (!ignoreZip && !this.hasOnlyBackendErrors('zipCode')) return false;
    if (!ignoreStreet && !this.hasOnlyBackendErrors('streetAddress')) return false;

    // All fields must have a value (except when ignored)
    if (!this.form.get('account')?.value) return false;
    if (!this.form.get('cardHolderName')?.value) return false;
    if (!this.form.get('expirationDate')?.value) return false;
    if (!ignoreCVV && !this.form.get('cvv')?.value) return false;
    if (!ignoreZip && !this.form.get('zipCode')?.value) return false;
    if (!ignoreStreet && !this.form.get('streetAddress')?.value) return false;

    if (this.authStore?.isLoggedIn() == false) return false;

    return true;
  }

  checkCard(trigger: 'auto' | 'manual' | 'swipe' = 'auto') {
    if (this.isEditMode) return;
    if (!this.isLocallyValid()) return;

    const force = trigger !== 'auto';
    const ignoreStreet = this.form.get('ignoreStreet')?.value == true;
    let cardNumber = this.form.get('account')?.value || '';
    const currentStreet = ignoreStreet ? '' : this.form.get('streetAddress')?.value || '';

    // Dedup: skip if nothing changed since the last verification (manual/swipe bypass this).
    if (
      !force &&
      this.lastChecked.account == cardNumber &&
      this.lastChecked.cardHolderName == this.form.get('cardHolderName')?.value &&
      this.lastChecked.expirationDate == this.form.get('expirationDate')?.value &&
      this.lastChecked.zipCode == this.form.get('zipCode')?.value &&
      this.lastChecked.cvv == this.form.get('cvv')?.value &&
      this.lastChecked.streetAddress == currentStreet
    ) {
      return;
    }

    // Auto-once gate: the card auto-verifies at most once (per PAN). Any further
    // re-verification must be user-initiated via the Verify Card button.
    if (trigger === 'auto' && this.hasAutoVerified) {
      return;
    }

    this.hasAutoVerified = true;
    this.lastVerifiedPan = cardNumber.replace(/\D/g, '');
    this.lastChecked.account = cardNumber || '';
    this.lastChecked.cardHolderName = this.form.get('cardHolderName')?.value || '';
    this.lastChecked.expirationDate = this.form.get('expirationDate')?.value || '';
    this.lastChecked.streetAddress = currentStreet;
    this.lastChecked.zipCode = this.form.get('zipCode')?.value || '';
    this.lastChecked.cvv = this.form.get('cvv')?.value || '';

    const ignoreCVV = this.form.get('ignoreCVV')?.value == true;
    const ignoreZip = this.form.get('ignoreZip')?.value == true;

    const paymentAccount: any = {
      requestType: 'CARD',
      account: cardNumber || '',
      cardHolderName: this.form.get('cardHolderName')?.value || '',
      expirationDate: this.form.get('expirationDate')?.value || '',
    };
    if (!ignoreCVV) paymentAccount.cvv = this.form.get('cvv')?.value || '';
    if (!ignoreStreet) paymentAccount.streetAddress = this.form.get('streetAddress')?.value || '';
    if (!ignoreZip) paymentAccount.zipCode = this.form.get('zipCode')?.value || '';
    if (ignoreCVV) paymentAccount.ignoreCVV = true;
    if (ignoreStreet) paymentAccount.ignoreStreet = true;
    if (ignoreZip) paymentAccount.ignoreZip = true;

    this.isVerifyingCard = true;
    this.secureClient
      .validatePayment(paymentAccount)
      .subscribe(
        (response: any) => {
          this.isVerifyingCard = false;
          this.processingResponse = true;

          this.isDebitRefused = isDebitNotAccepted(response, this.blockDebitCards);
          this.isCardInvalid = !response.validCard || this.isDebitRefused;
          this.isCvvInvalid = response.validCVV === false;
          this.hasCvvBeenChecked = true;
          if (this.isCvvInvalid) this.showIgnoreCVV = true;
          this.isStreetInvalid = response.validAVSStreet === false;
          this.hasStreetBeenChecked = true;
          if (this.isStreetInvalid) this.showIgnoreStreet = true;
          this.isZipInvalid = response.validAVSZip === false;
          this.hasZipBeenChecked = true;
          if (this.isZipInvalid) this.showIgnoreZip = true;
          this.isAvsInvalid = this.isStreetInvalid || this.isZipInvalid;
          this.cardType = response.cardMetadata?.cardType || '';
          this.cardFunding = response.cardMetadata?.debitCreditType || '';
          this.cardBank = response.cardMetadata?.fundingBank || '';
          const enteredAccount = (this.form.get('account')?.value || '').toString().replace(/\D/g, '');
          this.cardBin = response.cardMetadata?.cardBin || (enteredAccount.length >= 6 ? enteredAccount.substring(0, 6) : '');
          this.form.controls.account.updateValueAndValidity();
          this.form.controls.zipCode.updateValueAndValidity();
          this.form.controls.cvv.updateValueAndValidity();
          this.form.controls.streetAddress.updateValueAndValidity();
          this.processingResponse = false;

          if (this.isDebitRefused) {
            this.isCardValidated = false;
            this.invalidMessage = DEBIT_NOT_ACCEPTED_MESSAGE;
          } else if (this.isCardInvalid) {
            this.isCardValidated = false;
            this.invalidMessage = cardDeclinedMessage(response?.message);
          } else if (this.isCvvInvalid && !this.form.get('ignoreCVV')?.value) {
            this.isCardValidated = false;
            this.invalidMessage = 'CVV Verification Failed';
          } else if (this.isZipInvalid && !this.form.get('ignoreZip')?.value) {
            this.isCardValidated = false;
            this.invalidMessage = 'Zip Verification Failed';
          } else if (
            this.isStreetInvalid &&
            !this.form.get('ignoreStreet')?.value &&
            !this.form.get('ignoreZip')?.value
          ) {
            this.isCardValidated = false;
            this.invalidMessage = 'Address Verification Failed';
          } else {
            this.isCardValidated = true;
            this.invalidMessage = `${this.cardType} Card Verified`;
          }
        },
        (error: any) => {
          this.isVerifyingCard = false;
          this.processingResponse = true;
          this.isCardValidated = false;

          // A server-side (50x) error is NOT a card/CVV/AVS problem. Surface a generic message and
          // clear the field-level flags so we don't misleadingly show "Card Verification Failed".
          const httpStatus = error?.httpStatus ?? error?.code;
          if (typeof httpStatus === 'number' && httpStatus >= 500) {
            this.isCardInvalid = false;
            this.isCvvInvalid = false;
            this.isStreetInvalid = false;
            this.isZipInvalid = false;
            this.isAvsInvalid = false;
            this.invalidMessage = 'Something went wrong on our end and the card could not be verified. Please try again in a moment.';
            this.form.controls.account.updateValueAndValidity();
            this.form.controls.zipCode.updateValueAndValidity();
            this.form.controls.cvv.updateValueAndValidity();
            this.form.controls.streetAddress.updateValueAndValidity();
            this.processingResponse = false;
            return;
          }

          const details = error.data?.details || {};
          this.invalidMessage = '';
          this.isCardInvalid = !details.validCard;

          this.isCvvInvalid = details.validCVV === false;
          this.hasCvvBeenChecked = true;
          if (this.isCvvInvalid) this.showIgnoreCVV = true;
          this.isStreetInvalid = details.validAVSStreet === false;
          this.hasStreetBeenChecked = true;
          if (this.isStreetInvalid) this.showIgnoreStreet = true;

          this.isZipInvalid = details.validAVSZip === false;
          this.hasZipBeenChecked = true;
          if (this.isZipInvalid) this.showIgnoreZip = true;
          this.isAvsInvalid = this.isStreetInvalid || this.isZipInvalid;

          if (this.isCardInvalid) {
            this.isCardValidated = false;
            this.invalidMessage = cardDeclinedMessage(error?.data?.message);
          } else if (this.isCvvInvalid && !this.form.get('ignoreCVV')?.value) {
            this.isCardValidated = false;
            this.invalidMessage = 'CVV Verification Failed';
          } else if (this.isZipInvalid && !this.form.get('ignoreZip')?.value) {
            this.isCardValidated = false;
            this.invalidMessage = 'Zip Verification Failed';
          } else if (
            this.isStreetInvalid &&
            !this.form.get('ignoreStreet')?.value &&
            !this.form.get('ignoreZip')?.value
          ) {
            this.isCardValidated = false;
            this.invalidMessage = 'Address Verification Failed';
          } else {
            this.isCardValidated = true;
            this.invalidMessage = `${this.cardType} Card Verified`;
          }
          this.cardType = '';
          this.cardFunding = '';
          this.cardBank = '';
          this.form.controls.account.updateValueAndValidity();
          this.form.controls.zipCode.updateValueAndValidity();
          this.form.controls.cvv.updateValueAndValidity();
          this.form.controls.streetAddress.updateValueAndValidity();
          this.processingResponse = false;
        }
      );
  }
}
